import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { Box, Backdrop, useTheme } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import { useModal } from 'src/hooks/useModal';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { decrypt } from 'src/utils/hashCode';

import { genericApiToaster } from 'src/services/utils';
import { fetchStatuses } from 'src/services/status.service';
import { getDepartmentById } from 'src/services/department.service';
import { updateTicket, getTicketById } from 'src/services/ticket.service';
import { fetchUsersWithPermissions } from 'src/services/ticket.permissions.service';
import {
  getUserById,
  getUserAvatarUrl,
  getUsersByDepartment,
} from 'src/services/user.service';
import {
  fetchMessagesByTicketId,
  fetchCommentsPerTicketId,
} from 'src/services/conversation.service';

import Loader from 'src/components/loaders/Loader';

import { styles } from './styles';
import TicketHeader from './subSections/TicketHeader';
import FormAnswersModal from './modals/FormAnswersModal';
import ContextSidebar from './subSections/ContextSidebar';
import AssignTicketModal from './modals/AssignTicketModal';
import ConfirmationDialog from './dialogs/ConfirmationDialog';
import DelegateTicketModal from './modals/DelegateTicketModal';
import ManagementSidebar from './subSections/ManagementSidebar';
import AttachmentsListModal from './modals/AttachmentsListModal';
import AssignTicketToSelfModal from './modals/AssignToSelfModal';
import AddUserPermissionModal from './modals/AddUserPermissionModal';
import TicketConversation from './ticketConversation/TicketConversation';

export default function TicketDetailsView() {
  const { t } = useTranslation();
  const { ticketId } = useParams();

  const { id: currentUserId, departmentIds, permissions } = useAuth();

  const navigate = useNavigate();
  const { showToast, hideToast } = useToast();
  const { showModal, hideModal } = useModal();
  const { isSmallScreen } = useResponsiveScreen();

  const [ticketData, setTicketData] = useState(null);
  const [commentsData, setCommentsData] = useState([]);
  const [statusesList, setStatusesList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [permissionUsers, setPermissionUsers] = useState([]);
  const theme = useTheme();

  // Persistent, mutable in-memory caches to prevent N+1 query
  const userCacheRef = useRef({}); // Cache structure: { userId: AppUser }
  const userPromisesRef = useRef({}); // Active pending promises: { userId: Promise }

  const [isRightPanelOpen, setRightPanelOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    field: '',
    value: null,
    label: '',
  });

  const [customerData, setCustomerData] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [departmentData, setDepartmentData] = useState(null);
  const [accessRevokedOpen, setAccessRevokedOpen] = useState(false);

  const decryptedId = useMemo(() => {
    const raw = ticketId ? decrypt(ticketId) : null;
    if (!raw) return null;
    const clean = String(raw).trim();
    const numericId = Number(clean);
    return !Number.isNaN(numericId) ? numericId : null;
  }, [ticketId]);

  const canChat = useMemo(() => {
    if (!ticketData) return false;
    if (ticketData.createdBy === currentUserId) return true;
    if (ticketData.assignedTo === currentUserId) return true;

    if (departmentIds && departmentIds.includes(ticketData.departmentId)) return true;

    const guestPerm = ticketData.userPermission?.[currentUserId];
    return guestPerm && guestPerm[1] === true;
  }, [ticketData, currentUserId, departmentIds]);

  const handleShowAssignSelf = () => {
    showModal({
      title: t('pages.assignTicket.assignTicketToSelf'),
      Component: AssignTicketToSelfModal,
      props: {
        hideModal,
        assignTicket: (agentId, statusId) =>
          genericApiToaster(
            updateTicket,
            { ...ticketData, assignedTo: agentId, status_id: statusId },
            t('successMessages.successUpdate'),
            '',
            showToast,
            hideToast,
            t,
            (updated) => {
              setTicketData(updated); // Update local state directly—no refetching!
              hideModal();
            }
          ),
        ticketId: decryptedId,
        statusesList: statusesList.filter(
          (status) => status.systemMappedStatus === 'OPEN'
        ),
        currentUserId,
      },
    });
  };

  const safeFetchUser = useCallback(async (userId, callback) => {
    const id = Number(userId);
    let resolvedUser;

    if (id === -1 || id === 0) {
      resolvedUser = {
        id: -1,
        username: 'Anonymous User',
        email: 'N/D',
        givenName: 'Anonymous',
        familyName: 'User',
      };
    } else if (userCacheRef.current[id]) {
      // 1. Instant Cache Hit: Return from memory
      resolvedUser = userCacheRef.current[id];
    } else if (userPromisesRef.current[id]) {
      // 2. Promise Deduplication: Wait for the active network request to finish
      resolvedUser = await userPromisesRef.current[id];
    } else {
      // 3. Fresh Fetch: Launch exactly 1 network request and save the promise
      const promise = getUserById(id)
        .then((res) => {
          userCacheRef.current[id] = res;
          delete userPromisesRef.current[id]; // Cleanup pending map on success
          return res;
        })
        .catch((err) => {
          delete userPromisesRef.current[id]; // Cleanup pending map on failure
          throw err;
        });

      userPromisesRef.current[id] = promise;
      resolvedUser = await promise;
    }

    // Safely execute the callback to update the calling component's state
    if (callback && typeof callback === 'function') {
      callback(resolvedUser);
    }
    return resolvedUser;
  }, []);

  const loadTicket = useCallback(
    async (isInitial = false) => {
      if (!decryptedId) return;
      if (isInitial) setIsLoading(true);
      try {
        // 1. Fetch the ticket details first, and conditionally load guest permissions
        const hasCollabViewPermission = permissions?.includes('TICKET_ASSIGNEES:READ');

        const [ticket, guestsRes] = await Promise.all([
          getTicketById(decryptedId),
          hasCollabViewPermission
            ? fetchUsersWithPermissions(decryptedId)
            : Promise.resolve([]), // Safely bypasses the 403 endpoint if they don't have read permission
        ]);

        // 2. Query statuses scoped strictly to the department of this specific ticket
        const statuses = await fetchStatuses(
          ticket.departmentId ? [Number(ticket.departmentId)] : []
        );

        // 3. Continue fetching message data
        const [chatRes, commentsRes] = await Promise.all([
          fetchMessagesByTicketId(decryptedId),
          fetchCommentsPerTicketId(decryptedId),
        ]);

        const merged = [...(chatRes.data || []), ...(commentsRes.data || [])].sort(
          (a, b) => new Date(a.sentAt) - new Date(b.sentAt)
        );

        const mappedGuests = (guestsRes || []).map((u) => ({
          ...u,
          firstName: u.givenName,
          lastName: u.familyName,
          profilePic: u.avatarVersion ? getUserAvatarUrl(u.id, u.avatarVersion) : null,
        }));

        ticket.comments = merged;
        setStatusesList(statuses || []);
        setTicketData(ticket);
        setCommentsData(merged);
        setPermissionUsers(mappedGuests);

        if (ticket.departmentId) {
          const users = await getUsersByDepartment(ticket.departmentId);
          const activeUsers = (users || []).filter((u) => !u.archived);

          const mappedUsers = activeUsers.map((u) => ({
            ...u,
            avatarUrl: u.avatarVersion ? getUserAvatarUrl(u.id, u.avatarVersion) : null,
          }));
          setDepartmentUsers(mappedUsers);
        }
      } catch (e) {
        console.error('Diagnostic: API Bridge Failure', e);
        // FIXED: Only trigger lockout modal on true 403 Forbidden or 401 Unauthorized responses
        if (e.response?.status === 403 || e.response?.status === 401) {
          setAccessRevokedOpen(true);
        } else {
          showToast({
            message:
              t('errorMessages.couldntLoadTicket') ||
              'Failed to load ticket details. Please try again.',
            severity: 'error',
          });
        }
      } finally {
        if (isInitial) setIsLoading(false);
      }
    },
    [decryptedId, showToast, t, permissions]
  );

  const handleSilentRefresh = useCallback(() => {
    setTimeout(() => {
      loadTicket(false);
    }, 300);
  }, [loadTicket]);

  const creatorId = ticketData?.createdBy;
  const assigneeId = ticketData?.assignedTo;
  const deptId = ticketData?.departmentId;
  const creatorEmail = ticketData?.createdByEmail;

  useEffect(() => {
    // Only execute if a valid creator ID is available (ignores complex object reference)
    if (creatorId !== undefined && creatorId !== null) {
      const numericCreatorId = Number(creatorId);

      if (numericCreatorId === -1 || numericCreatorId === 0) {
        setCustomerData({
          id: -1,
          username: 'Anonymous User',
          email: creatorEmail || 'N/A',
          givenName: 'Anonymous',
          familyName: 'User',
          avatarUrl: null,
        });
      } else {
        genericApiToaster(getUserById, creatorId, '', '', null, null, t, (res) => {
          if (res) {
            setCustomerData({
              ...res,
              avatarUrl: res.avatarVersion
                ? getUserAvatarUrl(res.id, res.avatarVersion)
                : null,
            });
          }
        });
      }

      if (assigneeId && assigneeId !== -1) {
        genericApiToaster(getUserById, assigneeId, '', '', null, null, t, (res) => {
          if (res) {
            setAgentData({
              ...res,
              avatarUrl: res.avatarVersion
                ? getUserAvatarUrl(res.id, res.avatarVersion)
                : null,
            });
          }
        });
      }

      if (deptId) {
        genericApiToaster(
          getDepartmentById,
          deptId,
          '',
          '',
          null,
          null,
          t,
          setDepartmentData
        );
      }
    }
  }, [creatorId, assigneeId, deptId, creatorEmail, t]);

  useEffect(() => {
    const handleTicketUpdate = (event) => {
      const updatedId = event.detail?.ticketId;
      // If the notified ticket ID matches the active decrypted ID, re-fetch details
      if (updatedId && String(updatedId) === String(decryptedId)) {
        loadTicket(false);
      }
    };

    window.addEventListener('ticket-updated', handleTicketUpdate);
    return () => {
      window.removeEventListener('ticket-updated', handleTicketUpdate);
    };
  }, [decryptedId, loadTicket]);

  useEffect(() => {
    loadTicket(true);
  }, [loadTicket]);

  const handleShowAddUsers = () => {
    showModal({
      title: t('ticket.addUsersToTicket'),
      Component: AddUserPermissionModal,
      props: {
        ticketId: decryptedId,
        onRefresh: () => loadTicket(false),
        existingUsers: permissionUsers,
        hideModal,
      },
    });
  };

  const handleTriggerUpdate = (field, value, label) =>
    setConfirmDialog({ open: true, field, value, label });

  const handleConfirmUpdate = async () => {
    const { field, value } = confirmDialog;
    try {
      const updated = await updateTicket({ ...ticketData, [field]: value });
      setTicketData(updated); // Update local state directly—no refetching!
      showToast({ message: t('successMessages.successUpdate'), severity: 'success' });
    } finally {
      setConfirmDialog({ open: false, field: '', value: null, label: '' });
    }
  };

  const handleShowAnswers = () =>
    showModal({
      title: t('pages.ticketDetails.formResponses'),
      Component: FormAnswersModal,
      props: { row: ticketData, hideModal },
    });

  const handleShowFiles = () =>
    showModal({
      title: t('pages.ticketDetails.listAttachments'),
      Component: AttachmentsListModal,
      props: {
        ticketId: decryptedId,
        hideModal,
        ticketFilesList: ticketData?.attachments || [],
      },
    });

  const handleShowAssign = () => {
    showModal({
      title: t('pages.assignTicket.assignTicketTo'),
      Component: AssignTicketModal,
      props: {
        assignTicket: (agentId, statusId) =>
          genericApiToaster(
            updateTicket,
            { ...ticketData, assignedTo: agentId, status_id: statusId },
            t('successMessages.successUpdate'),
            '',
            showToast,
            hideToast,
            t,
            (updated) => {
              setTicketData(updated); // Update local state directly—no refetching!
              hideModal();
            }
          ),
        usersList: departmentUsers,
        department: departmentData?.name || '',
        statusesList,
        creatorId: ticketData?.createdBy,
        hideModal,
      },
    });
  };

  const handleShowReassign = () =>
    showModal({
      title: t('pages.assignTicket.reassignTicket'),
      Component: DelegateTicketModal,
      props: {
        delegateTicket: (data) =>
          genericApiToaster(
            updateTicket,
            data,
            t('successMessages.successUpdate'),
            '',
            showToast,
            hideToast,
            t,
            (updated) => setTicketData(updated) // Update local state directly—no refetching!
          ),
        usersList: departmentUsers,
        department: departmentData?.name || '',
        ticketData,
        isDelegate: false,
      },
    });

  // 1. Loading Gate: Show the spinner during initial fetch
  if (isLoading && !ticketData && !accessRevokedOpen) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Loader size={175} hideText />
      </Box>
    );
  }

  // 2. Security Gate: If access is revoked/denied, render ONLY the lock dialog (Blocks unauthorized data paints)
  if (accessRevokedOpen) {
    return (
      <ConfirmationDialog
        open={accessRevokedOpen}
        onClose={() => {
          setAccessRevokedOpen(false);
          navigate('/dashboard/tickets');
        }}
        onConfirm={() => {
          setAccessRevokedOpen(false);
          navigate('/dashboard/tickets');
        }}
        title={t('tickettt.accessRevokedTitle') || 'Access Revoked'}
        message={
          t('tickettt.accessRevokedMessage') ||
          'You do not have permission to view this ticket.'
        }
      />
    );
  }

  // 3. Render Tree (Only compiles if access is successfully authorized)
  return (
    <Box sx={styles.columnLayout}>
      <Backdrop sx={styles.loadingBackdrop} open={isLoading && !ticketData}>
        <Loader size={175} hideText />
      </Backdrop>

      <Box
        sx={{
          width: '21%',
          minWidth: '300px',
          flexShrink: 0,
          borderRight: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          display: isSmallScreen ? 'none' : 'block',
        }}
      >
        <ContextSidebar
          ticket={ticketData}
          customerData={customerData}
          agentData={agentData}
          departmentData={departmentData}
          onReassign={handleShowReassign}
          onAssign={handleShowAssign}
          onAssignSelf={handleShowAssignSelf}
          onShowAnswers={handleShowAnswers}
          onBack={() => navigate(-1)}
          hasWritePermission={canChat}
          canViewCollab={permissions?.includes('TICKET_ASSIGNEES:READ')}
        />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          bgcolor: 'background.paper',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          borderLeft: `1px solid ${theme.palette.divider}`,
        }}
      >
        <TicketHeader
          ticket={ticketData}
          statuses={statusesList}
          isRightPanelOpen={isRightPanelOpen}
          onUpdate={handleTriggerUpdate}
          onToggleRight={() => setRightPanelOpen(!isRightPanelOpen)}
          onShowAnswers={handleShowAnswers}
          onShowFiles={handleShowFiles}
          onBack={() => navigate(-1)}
          hasUpdatePermission={canChat}
        />
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <TicketConversation
            row={ticketData}
            ticketId={decryptedId}
            statusesList={statusesList}
            commentsData={commentsData}
            setCommentsData={setCommentsData}
            fetchUser={safeFetchUser}
            departmentUsers={departmentUsers}
            hasWritePermission={canChat}
            onSilentRefresh={handleSilentRefresh}
          />
        </Box>
      </Box>

      <Box
        sx={{
          width: isRightPanelOpen ? '26%' : '0px',
          flexShrink: 0,
          minWidth: isRightPanelOpen ? '320px' : '0px',
          borderLeft: isRightPanelOpen ? `1px solid ${theme.palette.divider}` : 'none',
          bgcolor: 'background.paper',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 11,
        }}
      >
        <Box
          sx={{
            width: '100%',
            minWidth: '320px',
            height: '100%',
            overflowY: 'auto',
            ...styles.scrollbarCustom,
          }}
        >
          <ManagementSidebar
            ticket={ticketData}
            permissionUsers={permissionUsers}
            onRefresh={() => loadTicket(false)}
            onUpdateTicketData={(updated) => setTicketData(updated)}
            onAddUsers={handleShowAddUsers}
            fetchUser={safeFetchUser}
            statusesList={statusesList}
            departmentUsers={departmentUsers}
            hasUpdatePermission={canChat}
            canViewCollab={permissions?.includes('TICKET_ASSIGNEES:READ')}
          />
        </Box>
      </Box>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, field: '', value: null, label: '' })
        }
        onConfirm={handleConfirmUpdate}
        title={t('ticket.confirmChangeTitle')}
        message={`${t('ticket.confirmChangeMessage')} ${confirmDialog.label}?`}
      />

      <ConfirmationDialog
        open={accessRevokedOpen}
        onClose={() => {
          setAccessRevokedOpen(false);
          navigate('/dashboard/tickets');
        }}
        onConfirm={() => {
          setAccessRevokedOpen(false);
          navigate('/dashboard/tickets');
        }}
        title={t('tickettt.accessRevokedTitle')}
        message={t('tickettt.accessRevokedMessage')}
      />
    </Box>
  );
}
